import { BaseTool } from '../BaseTool';
import { View } from '../../core/types';
import { computeDefaultViewRect, getSheetSize } from '../../rendering/SheetView';

/**
 * Sheet tool (View tab > Sheet Composition > Sheet).
 *
 * Two behaviors, like BIM:
 * 1. No sheet open  -> opens the "New Sheet" dialog flow: picks the next
 *    available number (A101, A102, ...) and creates the sheet with an A1
 *    title block, then opens it.
 * 2. A sheet open   -> arms "Place View" mode: the next click on the sheet
 *    places the selected Project Browser view (or the first model view if
 *    none selected) at the clicked position.
 */
export class SheetTool extends BaseTool {
    id = 'VIEW_SHEET';
    name = 'Sheet';
    cursor = 'default';

    onActivate(): void {
        const bim = this.context?.bimStore;
        const app = this.context?.appStore;
        if (!bim || !app) return;

        const project = bim.project;

        if (!project.activeSheetId) {
            // No sheet open: open the New Sheet dialog (template gallery) like
            // BIM's "New Sheet" dialog. The dialog creates + opens the sheet.
            window.dispatchEvent(new CustomEvent('yap:open-new-sheet-dialog'));
        } else {
            this.placeSelectedView();
        }

        this.deactivate();
        app.setActiveTool('select');
        bim.setActiveTool('SELECT');
    }

    /** Place the currently selected view (from Project Browser) on the open sheet. */
    private placeSelectedView(): void {
        const bim = this.context?.bimStore;
        if (!bim) return;

        const project = bim.project;
        const sheet = project.activeSheetId ? project.sheets.get(project.activeSheetId) : null;
        if (!sheet) return;

        // Candidate view: first selected id that is a model view, else first FloorPlan/3D view
        const selectedIds = this.context?.appStore.selectedIds ?? [];
        const bimSelection = bim.selection;
        const candidates = [...selectedIds, ...bimSelection];

        let view: View | undefined;
        for (const id of candidates) {
            const v = project.views.get(id);
            if (v && (v.type === 'FloorPlan' || v.type === '3D' || v.type === 'CeilingPlan' || v.type === 'Section' || v.type === 'Elevation')) {
                view = v;
                break;
            }
        }
        if (!view) {
            view = Array.from(project.views.values()).find(v => v.type === 'FloorPlan' || v.type === '3D');
        }
        if (!view) {
            console.warn('No model view available to place on the sheet.');
            return;
        }

        // Default slot position derived from existing placements
        const size = getSheetSize(sheet);
        const rect = computeDefaultViewRect(view, size, sheet.viewsPlaced);
        bim.placeViewOnSheet(sheet.id, view.id, { x: rect.x, y: rect.y });
        console.log(`Placed view "${view.name}" on sheet ${sheet.number}`);
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
